'use client';

import { ChevronDown, ListFilter, Search } from 'lucide-react';
import { useLayoutEffect, useReducer, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { FanficCardSkeleton } from '@/components/fanfic-card-skeleton';
import { useDebounce } from '@/hooks/use-debounce';
import { FanficCard } from '@/features/fanfic-card/components/fanfic-card';
import { FanfictionFilters } from '@/features/fanfiction-filters/components/FanfictionFilters';
import { ListingPagination } from '@/features/seo-listing/components/ListingPagination';
import {
  SeoListingChrome,
  SeoListingFooter,
} from '@/features/seo-listing/components/seo-listing-chrome';
import { stripFandomPrefixFromPageName } from '@/features/seo-listing/utils/seo-listing';
import { useCatalogFilterOptions } from '@/features/seo-listing/hooks';
import type { SeoResolveData } from '@/features/seo-listing/types';
import { LISTING_SORT_FALLBACK_LABEL, LISTING_SORT_OPTIONS } from '../../consts';
import {
  useListingCatalogSeoRestore,
  useListingFanficsLoader,
  useListingFilterHandlers,
  useListingFilterOptionsSync,
  useListingNavigateEventListeners,
  useListingPopstateSync,
  useListingRestoreNavigation,
  useListingRestoreSnapshot,
  useListingSeoPending,
  useListingSeoRedirectBridge,
  useListingUrlSync,
} from '../../hooks';
import { filterReducer, type FilterState } from '../../store';
import {
  buildInitialFilterState,
  buildListingPaginationSearchParams,
  getBrowserPathname,
  getShownWorks,
} from '../../utils';
import type { FanfictionGridProps } from './FanfictionGrid.types';

const LISTING_SKELETON_KEYS = Array.from({ length: 20 }, (_, index) => index);

const getSortLabel = (sortBy: FilterState['sortBy']): string =>
  LISTING_SORT_OPTIONS.find((option) => option.value === sortBy)?.label ??
  LISTING_SORT_FALLBACK_LABEL;

export const FanfictionGrid = ({
  isOriginals = false,
  useNewBackend = false,
  initialFanfics,
  initialTotalPages,
  initialTotalItems,
  initialFandoms,
  initialStatuses,
  initialRatings,
  initialTags,
  initialUniverses,
  initialCharacters,
  // Initial filter values taken from the URL
  initialPage = 1,
  initialSort = 'hotNew',
  initialSearch = '',
  initialSelectedFandoms = [],
  initialSelectedTags = [],
  initialSelectedUniverses = [],
  initialSelectedStatuses = [],
  initialSelectedRatings = [],
  initialSelectedCharacters = [],
  initialSelectedPairingCharacters = [],
  initialSeoPage = null,
  fandomsCount = null,
}: FanfictionGridProps) => {
  const pathname = usePathname();
  const basePath = isOriginals ? '/originals' : '/fanfics';
  const listingRestoreSnapshot = useListingRestoreSnapshot(basePath);

  // Initialize filter state from SSR props only. Listing-restore snapshots are
  // applied after mount (see useListingUrlSync) so hydration matches SSR.
  const [filterState, dispatch] = useReducer(
    filterReducer,
    {
      initialPage,
      initialSort,
      initialSearch,
      initialSelectedFandoms,
      initialSelectedTags,
      initialSelectedUniverses,
      initialSelectedStatuses,
      initialSelectedRatings,
      initialSelectedCharacters,
      initialSelectedPairingCharacters,
    },
    buildInitialFilterState,
  );

  // Latest filterState for async SEO/popstate callbacks. Synced in
  // useLayoutEffect so flushSync(dispatch) in onFiltersRestored updates the
  // ref before onNavigationSettled reads it.
  const filterStateRef = useRef(filterState);
  useLayoutEffect(() => {
    filterStateRef.current = filterState;
  }, [filterState]);

  // Must be created before syncRelatedFilterOptions — that helper skips
  // remembering path context while SEO redirect is in flight.
  const { isSeoPending, isSeoPendingRef, beginSeoNavigation, endSeoNavigation } =
    useListingSeoPending();

  const {
    options: filterOptions,
    isLoading: isFilterOptionsLoading,
    loadingFacets,
    reload: reloadFilterOptions,
  } = useCatalogFilterOptions({
    type: isOriginals ? 'original' : 'fanfic',
    initialOptions: {
      initialFandoms: initialFandoms ?? [],
      initialStatuses,
      initialRatings,
      initialTags: initialTags ?? [],
      initialUniverses: initialUniverses ?? [],
      initialCharacters: initialCharacters ?? [],
    },
  });

  const {
    syncRelatedFilterOptions,
    filterContextByPathRef,
    filterIdentityByPathRef,
    listingPathEpoch,
    setListingPathEpoch,
  } = useListingFilterOptionsSync({
    pathname,
    filterState,
    isSeoPendingRef,
    reloadFilterOptions,
    initialSelectedFandoms,
    initialSelectedTags,
    initialSelectedUniverses,
    initialSelectedCharacters,
    initialSelectedPairingCharacters,
  });

  // SEO permalink meta, patched manually because filter changes use
  // window.history.pushState (not router.push) and therefore do not trigger
  // Next.js Metadata API re-generation.
  // On Back from another Next route, listing restore can rewrite the URL to a
  // pushState permalink while RSC still serves the catalog tree — restore
  // seoPage from the snapshot so H1/chrome match the restored filters/URL.
  const [seoPage, setSeoPage] = useState<SeoResolveData | null>(initialSeoPage);
  const [listingWorksCount, setListingWorksCount] = useState<number | null>(
    initialTotalItems ?? null,
  );
  const [seoPageTitle, setSeoPageTitle] = useState<string | null>(initialSeoPage?.title ?? null);
  const { catalogSeoPageRef, restoreCatalogSeoPage } = useListingCatalogSeoRestore(
    basePath,
    initialSeoPage,
    setSeoPage,
    setSeoPageTitle,
  );
  // Display name for the single selected character — taken from the filter click
  // (or options when they load). Needed when the character stays checked via the
  // filters UI cache but drops out of filterOptions.initialCharacters after reload.
  const [selectedCharacterLabel, setSelectedCharacterLabel] = useState<string | undefined>();
  const seoPageTitleRef = useRef(seoPageTitle);
  useLayoutEffect(() => {
    seoPageTitleRef.current = seoPageTitle;
  }, [seoPageTitle]);
  const seoPageRef = useRef(seoPage);
  useLayoutEffect(() => {
    seoPageRef.current = seoPage;
  }, [seoPage]);

  // The SEO-pending guard (isSeoPendingRef / isSeoPending) blocks the updateURL
  // effect only for the duration of the SEO redirect itself (from
  // triggerSeoNavigation to onNavigationSettled). It is set synchronously BEFORE
  // dispatch in the fandoms/tags/characters handlers — otherwise the
  // intermediate render caused by that dispatch passes through updateURL before
  // the /api/v1/seo/redirect result is known and briefly writes a non-canonical
  // URL (e.g. /fanfics?fandoms=10529 instead of /fanfics/fandoms/naruto).
  // The React state duplicates the ref: SeoListingChrome keeps the previous H1
  // until resolve completes — otherwise the "Fandom «…»" template flashes for a
  // moment before the custom admin h1 arrives.

  const debouncedSearchQuery = useDebounce(filterState.searchQuery, 300);

  const isFirstRenderRef = useRef(true);
  const isPopstateNavigation = useRef(false);

  const { fanfics, totalPages, hasLoaded, isLoadingResults, error, setHasLoaded, setIsRefetching } =
    useListingFanficsLoader({
      isOriginals,
      filterState,
      debouncedSearchQuery,
      listingPathEpoch,
      isSeoPendingRef,
      isFirstRenderRef,
      listingRestoreSnapshot,
      initialFanfics,
      initialTotalPages,
      initialTotalItems,
      initialSeoPage,
      initialPage,
      listingWorksCount,
      setListingWorksCount,
      setSeoPage,
      setSeoPageTitle,
    });

  useListingRestoreNavigation({
    basePath,
    listingRestoreSnapshot,
    filterStateRef,
    seoPageTitleRef,
    seoPageRef,
    isFirstRenderRef,
    isPopstateNavigationRef: isPopstateNavigation,
    dispatch,
    setSeoPage,
    setSeoPageTitle,
    setHasLoaded,
    setIsRefetching,
    syncRelatedFilterOptions,
  });

  const { updateURL, syncStateFromUrl } = useListingUrlSync({
    filterState,
    dispatch,
    pathname,
    debouncedSearchQuery,
    isSeoPendingRef,
    isPopstateNavigationRef: isPopstateNavigation,
    seoPage,
    listingRestoreSnapshot,
  });

  useListingPopstateSync({
    filterStateRef,
    filterIdentityByPathRef,
    filterContextByPathRef,
    isPopstateNavigationRef: isPopstateNavigation,
    dispatch,
    setSeoPage,
    setSeoPageTitle,
    setListingPathEpoch,
    syncRelatedFilterOptions,
    syncStateFromUrl,
    restoreCatalogSeoPage,
  });

  const { triggerSeoNavigation, abortSeoNavigation } = useListingSeoRedirectBridge({
    basePath,
    filterStateRef,
    filterIdentityByPathRef,
    dispatch,
    setSeoPage,
    setSeoPageTitle,
    restoreCatalogSeoPage,
    endSeoNavigation,
    updateURL,
    syncRelatedFilterOptions,
  });

  const {
    handleFandomsChange,
    handleTagsChange,
    handleUniversesChange,
    handleStatusesChange,
    handleRatingsChange,
    handleDirectionsChange,
    handleCharactersChange,
    handleCharacterCardNavigate,
    handlePageChange,
    handleCatalogNavigate,
    handleFandomHrefNavigate,
  } = useListingFilterHandlers({
    filterState,
    filterStateRef,
    dispatch,
    isOriginals,
    basePath,
    pathname,
    debouncedSearchQuery,
    beginSeoNavigation,
    endSeoNavigation,
    triggerSeoNavigation,
    abortSeoNavigation,
    updateURL,
    restoreCatalogSeoPage,
    catalogSeoPageRef,
    setSelectedCharacterLabel,
    syncRelatedFilterOptions,
  });

  useListingNavigateEventListeners({
    onFandomsChange: handleFandomsChange,
    onCharacterCardNavigate: handleCharacterCardNavigate,
    onCatalogNavigate: handleCatalogNavigate,
    onFandomHrefNavigate: handleFandomHrefNavigate,
  });

  const selectedFandom = filterOptions.initialFandoms.find(
    (fandom) => String(fandom.id) === String(filterState.selectedFandoms[0]),
  );
  const selectedCharacter = filterOptions.initialCharacters.find(
    (character) => String(character.id) === String(filterState.selectedCharacters[0]),
  );
  const selectedPairingCharacter = filterOptions.initialCharacters.find(
    (character) => String(character.id) === String(filterState.selectedPairingCharacters[0]),
  );
  // Click label is only valid while exactly one character stays selected.
  const activeCharacterLabel =
    filterState.selectedCharacters.length === 1 ? selectedCharacterLabel : undefined;
  const seoPageType = seoPage?.pageType as string | undefined;
  const listingFandomTitle =
    filterState.selectedFandoms.length === 1
      ? (selectedFandom?.name ??
        selectedCharacter?.fandomTitle ??
        selectedPairingCharacter?.fandomTitle)
      : undefined;
  const listingEntityName =
    filterState.selectedCharacters.length === 1
      ? (selectedCharacter?.name ?? activeCharacterLabel)
      : seoPageType === 'pairing' || seoPageType === 'fandom_pairing'
        ? stripFandomPrefixFromPageName(seoPage?.pageName ?? '', listingFandomTitle ?? '')
        : undefined;
  const shouldRenderListingChrome = true;
  const schemaPathname = typeof window !== 'undefined' ? getBrowserPathname() : pathname;
  const listingSearchParams = buildListingPaginationSearchParams({
    pathname: schemaPathname,
    sortBy: filterState.sortBy,
    searchQuery: debouncedSearchQuery,
    selectedFandoms: filterState.selectedFandoms,
    selectedTags: filterState.selectedTags,
    selectedUniverses: filterState.selectedUniverses,
    selectedStatuses: filterState.selectedStatuses,
    selectedRatings: filterState.selectedRatings,
    selectedCharacters: filterState.selectedCharacters,
    selectedPairingCharacters: filterState.selectedPairingCharacters,
  });
  const shownWorks = getShownWorks({
    fanfics,
    isLoadingResults,
    hasError: Boolean(error),
    pathname: schemaPathname,
    isOriginals,
  });

  // Shared by the desktop sidebar and the mobile action sheet
  const filtersProps = {
    isOriginals,
    useNewBackend,
    selectedFandoms: filterState.selectedFandoms,
    selectedTags: filterState.selectedTags,
    selectedUniverses: filterState.selectedUniverses,
    selectedStatuses: filterState.selectedStatuses,
    selectedRatings: filterState.selectedRatings,
    selectedDirections: filterState.selectedDirections,
    selectedCharacters: filterState.selectedCharacters,
    onFandomsChange: handleFandomsChange,
    onTagsChange: handleTagsChange,
    onUniversesChange: handleUniversesChange,
    onStatusesChange: handleStatusesChange,
    onRatingsChange: handleRatingsChange,
    onCharactersChange: handleCharactersChange,
    onSingleCharacterNavigate: handleCharacterCardNavigate,
    onDirectionsChange: handleDirectionsChange,
    initialFandoms: filterOptions.initialFandoms,
    initialStatuses: filterOptions.initialStatuses,
    initialRatings: filterOptions.initialRatings,
    initialTags: filterOptions.initialTags,
    initialUniverses: filterOptions.initialUniverses,
    initialCharacters: filterOptions.initialCharacters,
    isOptionsLoading: isFilterOptionsLoading,
    loadingFacets,
  };

  return (
    <div className="container mx-auto px-[10px] xs:px-4 py-6">
      {shouldRenderListingChrome ? (
        <SeoListingChrome
          pathname={pathname}
          seoPage={seoPage}
          listingWorksCount={listingWorksCount}
          fandomsCount={isOriginals ? null : fandomsCount}
          selectedFandoms={filterState.selectedFandoms}
          selectedCharacters={filterState.selectedCharacters}
          fandomTitle={listingFandomTitle}
          fandomPermanentUrl={selectedFandom?.permanentUrl}
          entityName={listingEntityName}
          isSeoPending={isSeoPending}
          section={isOriginals ? 'originals' : 'fanfics'}
          listingPage={filterState.currentPage}
          listingSearchParams={listingSearchParams}
          shownWorks={shownWorks}
        />
      ) : null}
      <div className="flex gap-[18px] lg:gap-5">
        {/* Main area */}
        <div className="flex-1">
          {/* Search and sorting */}
          <div className="flex items-center gap-1 sm:gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 text-black/50 h-4 w-4" />
              <Input
                placeholder="Что ищем сегодня?"
                value={filterState.searchQuery}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_SEARCH_QUERY',
                    payload: e.target.value,
                  })
                }
                className="pl-10 bg-white"
              />
            </div>
            {/* Filters button for mobile */}
            <Button
              variant="outline"
              className="md:hidden bg-white h-full py-3 border-none"
              onClick={() => dispatch({ type: 'SET_MOBILE_FILTERS_OPEN', payload: true })}
            >
              <span className="ml-2">Фильтры</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="whitespace-nowrap bg-white h-full py-3 border-none hover:outline hover:outline-2 hover:outline-offset-2 hover:outline-white"
                >
                  <ListFilter className="w-4 h-4" />
                  <span className="hidden sm:block">{getSortLabel(filterState.sortBy)}</span>
                  <ChevronDown className="text-[#C0C3D3] !size-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {LISTING_SORT_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => dispatch({ type: 'SET_SORT_BY', payload: option.value })}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Loading and error states */}
          {isLoadingResults && (
            <div className="grid grid-cols-2 min-[580px]:grid-cols-3 min-[1160px]:grid-cols-4 xl:grid-cols-5 lg:gap-5 gap-3 auto-rows-max">
              {LISTING_SKELETON_KEYS.map((key) => (
                <FanficCardSkeleton key={key} />
              ))}
            </div>
          )}

          {error && hasLoaded && (
            <div className="flex justify-center items-center py-12">
              <div className="text-red-500">Ошибка загрузки: {error}</div>
            </div>
          )}

          {!isLoadingResults && !error && hasLoaded && fanfics.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <div className="text-gray-500">По вашему запросу ничего не найдено</div>
            </div>
          )}

          {/* Fanfics grid */}
          {!isLoadingResults && !error && fanfics.length > 0 && (
            <div className="grid grid-cols-2 min-[580px]:grid-cols-3 min-[1160px]:grid-cols-4 xl:grid-cols-5 lg:gap-5 gap-3 auto-rows-max">
              {fanfics.map((fanfic) => (
                <FanficCard
                  key={fanfic.id}
                  {...fanfic}
                  listPathname={pathname}
                  detailPathPrefix={isOriginals ? '/originals' : undefined}
                  tags={fanfic.tags}
                  characters={fanfic.characters}
                  fandom={fanfic.fandom}
                />
              ))}
            </div>
          )}

          {/* SSR-friendly pagination: real <a href> links for crawlers */}
          {!isLoadingResults && !error && fanfics.length > 0 && totalPages > 1 && (
            <ListingPagination
              pathname={typeof window !== 'undefined' ? getBrowserPathname() : pathname}
              searchParams={listingSearchParams}
              currentPage={filterState.currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>

        {/* Desktop filters */}
        <div className="hidden md:block w-[190px] lg:w-[210px] shrink-0">
          <FanfictionFilters {...filtersProps} />
        </div>
      </div>

      {/* Mobile filters action sheet */}
      {filterState.isMobileFiltersOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => dispatch({ type: 'SET_MOBILE_FILTERS_OPEN', payload: false })}
          />
          {/* Action Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[calc(100vh-100px)] overflow-y-auto">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Фильтры</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    dispatch({
                      type: 'SET_MOBILE_FILTERS_OPEN',
                      payload: false,
                    })
                  }
                >
                  ✕
                </Button>
              </div>
              <FanfictionFilters {...filtersProps} />
            </div>
          </div>
        </div>
      )}
      {shouldRenderListingChrome ? (
        <SeoListingFooter
          pathname={pathname}
          seoPage={seoPage}
          fandomTitle={selectedFandom?.name}
        />
      ) : null}
    </div>
  );
};
